import type { ServiceArea } from "@tdelivr/api-client/generated/geo/service-area.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { at, field, type RecordDetail } from "./record-detail.ts";

/**
 * What the service-areas board does to the rows GEO-06 returns, away from any rendering.
 *
 * The same split as `offer-monitor.ts` and `geofences.ts`.
 *
 * **Two findings, and the second one corrects something already written down.**
 *
 * The first is that `lifecycle` is not coverage, and an area stored `active` is not necessarily
 * serving anybody: GEO-06's own availability evaluation requires the instant to be inside the
 * effective window, and both bounds are published on every row. `servingNow()` below holds it.
 *
 * The second is that **GEO-06's `PUT` is an upsert, so an area can be created from a console** —
 * which `apps/tenant-admin-console` reads the other way, disabling its own Add button because "the
 * only write is `PUT /v1/service-areas/{id}`, which needs an identifier and an `expectedVersion`
 * the form would have to invent". Measured against the service: `nextArea` compares
 * `input.current?.version ?? 0` with the `expectedVersion` given, so a `PUT` to an identifier that
 * does not exist carrying `expectedVersion: 0` creates the area and anything else is a 409. A
 * client-minted identifier is how a `PUT`-upsert is meant to be addressed, and `0` is not an
 * invention — it is the assertion "I believe this does not exist yet", which is the concurrency
 * check doing exactly its job. R-19 in `docs/design/DESIGN_RECONCILIATION.md` records both.
 */

/** GEO-06's published lifecycle enum, as one name. */
export type AreaLifecycle = ServiceArea["lifecycle"];

/** Every lifecycle the listing's own filter accepts, in the contract's order. */
export const FILTERABLE_LIFECYCLES: readonly AreaLifecycle[] = ["draft", "active", "inactive"];

/**
 * How many areas to take in one request.
 *
 * 200 is GEO-06's own cap, stated in the parameter's own description: "defaulting to 50 and capped
 * at 200. A limit above the cap is clamped rather than refused."
 */
export const PER_FETCH = "200";

/**
 * The expected version that asserts an area does not exist yet.
 *
 * Named rather than written as a literal `0` at the call site, because the number is a claim about
 * the world and not a default. GEO-06's `nextArea` reads an absent area's version as zero, so this
 * is the value that creates — and the same value against an area that does exist is refused with
 * `service_area_version_conflict`, which is what makes minting an identifier safe.
 */
export const VERSION_OF_NOTHING = 0;

/**
 * Whether this area is actually serving at a given instant.
 *
 * **GEO-06's own availability query is the authority, and `lifecycle` is only one of its three
 * clauses.** `POST /v1/service-areas/availability` — the operation that decides whether somebody
 * standing somewhere can be served — selects on
 *
 * ```sql
 * lifecycle='active' AND effective_from<=$at AND (effective_until IS NULL OR effective_until>$at)
 * ```
 *
 * So an area stored `active` with an `effectiveFrom` in the future, or an `effectiveUntil` in the
 * past, is not in the candidate set: the platform will not offer it and a request there gets
 * nothing, while its row still reads `active`. A dispatcher who believes a zone is open stops
 * looking for the reason work there is being refused, so this board does not draw the stored
 * lifecycle on its own.
 *
 * Only `active` areas are tested against the window. A draft area's dates are a plan and an
 * inactive one's are history; neither is a claim about now.
 */
export function servingNow(area: ServiceArea, now: number): boolean {
  if (area.lifecycle !== "active") return false;
  const from = Date.parse(area.effectiveFrom);
  if (Number.isFinite(from) && from > now) return false;
  if (area.effectiveUntil !== undefined) {
    const until = Date.parse(area.effectiveUntil);
    if (Number.isFinite(until) && until <= now) return false;
  }
  return true;
}

/** Which side of its window an area falls, when it is not inside it. */
export type WindowPosition = "inside" | "not_yet" | "ended";

export function windowPosition(area: ServiceArea, now: number): WindowPosition {
  const from = Date.parse(area.effectiveFrom);
  if (Number.isFinite(from) && from > now) return "not_yet";
  if (area.effectiveUntil !== undefined) {
    const until = Date.parse(area.effectiveUntil);
    if (Number.isFinite(until) && until <= now) return "ended";
  }
  return "inside";
}

/**
 * The state class and the word.
 *
 * **The design's three words are not this service's, and substituting them would be a false
 * statement rather than a translation.** `17 WP16 Tenant Admin.dc.html`, page `zones` — the only
 * board in the bundle that draws this subject at all — puts `served`, `partial` and
 * `review request` under a column headed `Coverage`. GEO-06 publishes
 * `lifecycle: draft | active | inactive`, which answers whether the *record* is in force rather
 * than how much service the area gets. `partial` in particular has no field anywhere on the row,
 * and it is the design's stated reason for the page existing.
 *
 * `classOfStatus` cannot answer this either: the lifecycle table in `packages/design-tokens` is
 * read out of `01 Token System v2.dc.html` and carries no GEO-06 entry, so a badge left to the
 * table would render every row unclassified. The classes are the design's own for the facts that do
 * correspond — `draft` is `idle`, `active` inside its window is `done`, `inactive` is `closed` — and
 * an `active` area outside its window is `caution`, the class whose label is "Qualified": in force
 * as a record and not in force as a service is exactly an outcome with a condition attached.
 */
export function stateOf(
  area: ServiceArea,
  now: number,
): { classKey: StateClassKey; label: string } {
  if (area.lifecycle === "draft") return { classKey: "idle", label: "draft" };
  if (area.lifecycle === "inactive") return { classKey: "closed", label: "inactive" };
  const position = windowPosition(area, now);
  if (position === "not_yet") return { classKey: "caution", label: "not yet effective" };
  if (position === "ended") return { classKey: "caution", label: "no longer effective" };
  return { classKey: "done", label: "active" };
}

/**
 * What an `active` area's badge needs to say beyond its word, or nothing.
 *
 * On the badge's `title` rather than in a column of its own, because a column the design does not
 * draw is design content invented here — the same call the offer monitor made for its lapse reason.
 */
export function stateReason(area: ServiceArea, now: number): string | undefined {
  if (area.lifecycle !== "active") return undefined;
  const position = windowPosition(area, now);
  if (position === "not_yet") {
    return `Stored active, and effective from ${area.effectiveFrom}. GEO-06's availability evaluation requires the instant to be inside the effective window, so nothing can be dispatched here yet.`;
  }
  if (position === "ended") {
    return `Stored active, and effective until ${area.effectiveUntil ?? "an instant now past"}. GEO-06's availability evaluation requires the instant to be inside the effective window, so nothing can be dispatched here any more.`;
  }
  return undefined;
}

/**
 * The design's `Coverage` cell, from the one coverage fact the row publishes.
 *
 * `productRefs` is what can be served in this area, and the design's own `Boda only` is the shape
 * for a single-product area. The references are published as opaque strings matching
 * `^[a-zA-Z0-9][a-zA-Z0-9._:-]{0,127}$`, and nothing on the platform turns one into a product's
 * name, so they are shown as published rather than prettified.
 *
 * The read schema puts no minimum on the array where the write schema requires at least one, so an
 * empty list is reachable on the wire and is said rather than drawn as an empty cell.
 */
export function coverageText(area: ServiceArea): string {
  if (area.productRefs.length === 0) return "no services configured";
  if (area.productRefs.length === 1) return `${area.productRefs[0]!} only`;
  return area.productRefs.join(" · ");
}

/**
 * How this area's boundary is defined, in one phrase.
 *
 * `boundary` is a union and the two halves are genuinely different facts: a `geometry` area carries
 * its own rings, and a `geofence` area defers to a GEO-05 fence at a pinned version. The second
 * matters to a dispatcher because amending that fence does not move this area — the version is part
 * of the reference — so the phrase names the version rather than only the fence.
 */
export function boundaryText(area: ServiceArea): string {
  if (area.boundary.kind === "geofence") {
    return `geofence ${area.boundary.geofenceId} · v${area.boundary.geofenceVersion}`;
  }
  const geometry = area.boundary.geometry;
  const rings =
    geometry.type === "Polygon" ? geometry.coordinates.length : geometry.coordinates.length;
  return `${geometry.type} · ${rings} ring${rings === 1 ? "" : "s"}`;
}

/** The sort options the design's toolbar offers, in its order and its words. */
export type AreaOrder = "default" | "state" | "az" | "za";

export interface AreaQuery {
  readonly search: string;
  readonly order: AreaOrder;
  /** 1-based, as the pager shows it. */
  readonly page: number;
  readonly perPage: number;
  /** Now, for the effective-window rule, so a search for "no longer effective" finds its row. */
  readonly now: number;
}

export interface AreaPage {
  readonly rows: readonly ServiceArea[];
  /** Every row that matched, across all pages — what an export writes. */
  readonly all: readonly ServiceArea[];
  readonly total: number;
  readonly pageCount: number;
}

/** Every word on a row a search should match: the published values and the words on screen. */
function searchableText(area: ServiceArea, now: number): string {
  return [
    area.name,
    area.marketRef,
    area.productRefs.join(" "),
    coverageText(area),
    area.lifecycle,
    stateOf(area, now).label,
    area.id,
    boundaryText(area),
  ]
    .join(" ")
    .toLowerCase();
}

/**
 * Filter, order and page what the screen holds.
 *
 * `default` is the order GEO-06 answered in — `ORDER BY priority DESC, area_id` — and the screen
 * must not quietly replace it. Precedence is what decides which area's rules apply where two
 * overlap, and the resolver breaks a tie the same way, so the board shows areas in the order the
 * platform will actually apply them. Sorted by name the page would be easier to scan and would hide
 * the thing it exists to show.
 *
 * There is no lifecycle filter here: GEO-06's listing takes one as a query parameter, so choosing a
 * lifecycle refetches rather than hiding rows the browser already holds.
 */
export function areaPage(rows: readonly ServiceArea[], query: AreaQuery): AreaPage {
  const needle = query.search.trim().toLowerCase();
  const matched =
    needle.length === 0
      ? [...rows]
      : rows.filter((area) => searchableText(area, query.now).includes(needle));

  const ordered =
    query.order === "default"
      ? matched
      : [...matched].sort((left, right) => {
          if (query.order === "state") {
            return stateOf(left, query.now).label.localeCompare(stateOf(right, query.now).label);
          }
          const compared = left.name.localeCompare(right.name);
          return query.order === "az" ? compared : -compared;
        });

  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / query.perPage));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const from = (page - 1) * query.perPage;

  return { rows: ordered.slice(from, from + query.perPage), all: ordered, total, pageCount };
}

/**
 * The one sentence this board owes an operator about what it fetched, or nothing.
 *
 * GEO-06 pages on a composite `(priority, area_id)` cursor, so following it is safe — but this
 * board takes one page of 200 rather than walking the whole list, and a tenant with more areas than
 * that is looking at the strongest 200 precedences rather than all of them. On a board whose whole
 * ordering is precedence, that is the one fact an operator must not be allowed to misread.
 */
export function boundedNote(
  fetched: number | undefined,
  truncated: boolean,
  lifecycle: AreaLifecycle | undefined,
  perFetch: number,
): string | undefined {
  if (fetched === undefined) return undefined;
  if (!truncated) return undefined;
  const scope = lifecycle === undefined ? "service areas" : `${lifecycle} service areas`;
  return `More than ${perFetch} ${scope} match. This board shows the ${perFetch} with the strongest precedence, so the count is a floor rather than a total.`;
}

/**
 * What an operator has typed into the add or edit form.
 *
 * Strings throughout, including the numbers and the GeoJSON, because that is what an `<input>` and a
 * `<textarea>` hold. Every field here is one GEO-06 publishes on `Definition`; the design draws
 * three more — `Coverage`, `Hours (if partial)` and `Reason shown to customers` — and none of the
 * three has a field anywhere on this service, so they are absent rather than collected and dropped.
 */
export interface AreaDraft {
  readonly name: string;
  readonly marketRef: string;
  /** One product reference per line, as the design's chips are one per product. */
  readonly productRefs: string;
  readonly priority: string;
  readonly lifecycle: AreaLifecycle;
  readonly effectiveFrom: string;
  readonly effectiveUntil: string;
  readonly boundaryKind: "geometry" | "geofence";
  /** `geometry` only: the GeoJSON the design's Boundary field says may be uploaded. */
  readonly geometry: string;
  /** `geofence` only. */
  readonly geofenceId: string;
  readonly geofenceVersion: string;
}

/** An empty draft, which is what the add form opens on. */
export const EMPTY_AREA_DRAFT: AreaDraft = {
  name: "",
  marketRef: "",
  productRefs: "",
  priority: "0",
  lifecycle: "draft",
  effectiveFrom: "",
  effectiveUntil: "",
  boundaryKind: "geometry",
  geometry: "",
  geofenceId: "",
  geofenceVersion: "",
};

/**
 * An area, as the form that edits it.
 *
 * The round trip has to be exact or an operator who raises a priority also rewrites the boundary:
 * `PUT` replaces the whole definition, so every field the form does not carry is one the amendment
 * silently changes.
 */
export function draftOf(area: ServiceArea): AreaDraft {
  const shared = {
    name: area.name,
    marketRef: area.marketRef,
    productRefs: area.productRefs.join("\n"),
    priority: String(area.priority),
    lifecycle: area.lifecycle,
    effectiveFrom: area.effectiveFrom,
    effectiveUntil: area.effectiveUntil ?? "",
  };
  if (area.boundary.kind === "geofence") {
    return {
      ...shared,
      boundaryKind: "geofence",
      geometry: "",
      geofenceId: area.boundary.geofenceId,
      geofenceVersion: String(area.boundary.geofenceVersion),
    };
  }
  return {
    ...shared,
    boundaryKind: "geometry",
    geometry: JSON.stringify(area.boundary.geometry, null, 2),
    geofenceId: "",
    geofenceVersion: "",
  };
}

/** One complaint about one field, in the words the form puts under it. */
export interface AreaProblem {
  readonly field: keyof AreaDraft;
  readonly message: string;
}

/** The shape `Definition.boundary` takes, with the coordinates typed as the write schema types them. */
export type DraftBoundary =
  | {
      readonly kind: "geometry";
      readonly geometry:
        | { readonly type: "Polygon"; readonly coordinates: number[][][] }
        | { readonly type: "MultiPolygon"; readonly coordinates: number[][][][] };
    }
  | {
      readonly kind: "geofence";
      readonly geofenceId: string;
      readonly geofenceVersion: number;
    };

/**
 * Parse a position array the way GEO-06's `ring` schema reads one.
 *
 * `[longitude, latitude]`, GeoJSON order — the opposite of GEO-05's `{latitude, longitude}` object,
 * which is worth stating because the two services sit on one page and a transposed pair puts a zone
 * in the sea without failing anything. The bounds are the contract's: longitude first, −180 to 180,
 * then latitude, −90 to 90.
 */
function position(value: unknown): number[] | undefined {
  if (!Array.isArray(value) || value.length !== 2) return undefined;
  const [longitude, latitude] = value as unknown[];
  if (typeof longitude !== "number" || !Number.isFinite(longitude)) return undefined;
  if (typeof latitude !== "number" || !Number.isFinite(latitude)) return undefined;
  if (longitude < -180 || longitude > 180 || latitude < -90 || latitude > 90) return undefined;
  return [longitude, latitude];
}

/** A closed linear ring of at least four positions, as the contract requires. */
function ring(value: unknown): number[][] | undefined {
  if (!Array.isArray(value) || value.length < 4) return undefined;
  const points: number[][] = [];
  for (const entry of value) {
    const point = position(entry);
    if (point === undefined) return undefined;
    points.push(point);
  }
  const first = points[0]!;
  const last = points.at(-1)!;
  if (first[0] !== last[0] || first[1] !== last[1]) return undefined;
  return points;
}

/**
 * The boundary a draft describes, or the reasons it describes none.
 */
export function boundaryOf(draft: AreaDraft): DraftBoundary | readonly AreaProblem[] {
  if (draft.boundaryKind === "geofence") {
    const problems: AreaProblem[] = [];
    if (!/^[0-9a-fA-F-]{36}$/.test(draft.geofenceId.trim())) {
      problems.push({ field: "geofenceId", message: "Geofence id must be a UUID." });
    }
    const version = Number(draft.geofenceVersion.trim());
    if (!Number.isInteger(version) || version < 1) {
      problems.push({
        field: "geofenceVersion",
        message: "Geofence version must be a whole number of 1 or more.",
      });
    }
    if (problems.length > 0) return problems;
    return {
      kind: "geofence",
      geofenceId: draft.geofenceId.trim(),
      geofenceVersion: version,
    };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(draft.geometry);
  } catch {
    return [{ field: "geometry", message: "Boundary must be valid JSON." }];
  }

  const shape = parsed as { type?: unknown; coordinates?: unknown };
  if (shape.type === "Polygon") {
    if (!Array.isArray(shape.coordinates) || shape.coordinates.length < 1) {
      return [{ field: "geometry", message: "A Polygon needs at least one ring." }];
    }
    const rings: number[][][] = [];
    for (const entry of shape.coordinates) {
      const parsedRing = ring(entry);
      if (parsedRing === undefined) {
        return [
          {
            field: "geometry",
            message:
              "Every ring needs four or more [longitude, latitude] positions and must repeat its first position last.",
          },
        ];
      }
      rings.push(parsedRing);
    }
    return { kind: "geometry", geometry: { type: "Polygon", coordinates: rings } };
  }

  if (shape.type === "MultiPolygon") {
    if (!Array.isArray(shape.coordinates) || shape.coordinates.length < 1) {
      return [{ field: "geometry", message: "A MultiPolygon needs at least one polygon." }];
    }
    const polygons: number[][][][] = [];
    for (const entry of shape.coordinates) {
      if (!Array.isArray(entry) || entry.length < 1) {
        return [{ field: "geometry", message: "Every polygon needs at least one ring." }];
      }
      const rings: number[][][] = [];
      for (const inner of entry) {
        const parsedRing = ring(inner);
        if (parsedRing === undefined) {
          return [
            {
              field: "geometry",
              message:
                "Every ring needs four or more [longitude, latitude] positions and must repeat its first position last.",
            },
          ];
        }
        rings.push(parsedRing);
      }
      polygons.push(rings);
    }
    return { kind: "geometry", geometry: { type: "MultiPolygon", coordinates: polygons } };
  }

  return [
    { field: "geometry", message: 'Boundary must be a GeoJSON "Polygon" or "MultiPolygon".' },
  ];
}

/** The body `PUT /v1/service-areas/{id}` takes, or why the draft cannot make one. */
export interface AreaDefinition {
  readonly name: string;
  readonly marketRef: string;
  readonly productRefs: string[];
  readonly priority: number;
  readonly lifecycle: AreaLifecycle;
  readonly effectiveFrom: string;
  readonly effectiveUntil?: string;
  readonly boundary: DraftBoundary;
  readonly expectedVersion: number;
}

/** GEO-06's own `ref` pattern, which every market and product reference has to match. */
const REFERENCE = /^[a-zA-Z0-9][a-zA-Z0-9._:-]{0,127}$/;

/**
 * The definition a draft describes, pinned to the version the screen was shown.
 *
 * **`expectedVersion` comes from the row the operator opened, or is `VERSION_OF_NOTHING` for a new
 * area.** It is not collected and not defaulted: it is the version the screen displayed, so an area
 * somebody else amended in the meantime is refused with `service_area_version_conflict` rather than
 * silently overwritten.
 *
 * Every bound below is GEO-06's own, read from the Zod schema its route validates with — a name of
 * 1 to 120 characters, references matching its `ref` pattern, 1 to 100 of them deduplicated and
 * sorted, a priority from −1000 to 1000, an ISO instant for each end of the window and an end after
 * the start. Restated rather than imported, because a console must not import a service's source;
 * asserted against the published contract in `tests/service-areas.test.ts` so the two cannot drift.
 */
export function definitionOf(
  draft: AreaDraft,
  expectedVersion: number,
): AreaDefinition | readonly AreaProblem[] {
  const problems: AreaProblem[] = [];

  const name = draft.name.trim();
  if (name.length < 1 || name.length > 120) {
    problems.push({ field: "name", message: "Name must be 1 to 120 characters." });
  }

  const marketRef = draft.marketRef.trim();
  if (!REFERENCE.test(marketRef)) {
    problems.push({
      field: "marketRef",
      message: "Region must start with a letter or digit and use only letters, digits and . _ : -",
    });
  }

  const references = draft.productRefs
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  // Deduplicated and sorted here because GEO-06's own schema transforms the array that way before
  // storing it. A form that sent them in typing order would produce a definition that differs from
  // what comes back, and the next edit would look like somebody else's change.
  const productRefs = [...new Set(references)].sort();
  if (productRefs.length < 1 || productRefs.length > 100) {
    problems.push({
      field: "productRefs",
      message: "List 1 to 100 product references, one per line.",
    });
  } else if (productRefs.some((reference) => !REFERENCE.test(reference))) {
    problems.push({
      field: "productRefs",
      message:
        "Every product reference must start with a letter or digit and use only letters, digits and . _ : -",
    });
  }

  const priority = Number(draft.priority.trim());
  if (!Number.isInteger(priority) || priority < -1000 || priority > 1000) {
    problems.push({
      field: "priority",
      message: "Priority must be a whole number from -1000 to 1000.",
    });
  }

  const from = draft.effectiveFrom.trim();
  if (from.length === 0 || !Number.isFinite(Date.parse(from))) {
    problems.push({
      field: "effectiveFrom",
      message: "Effective from must be an ISO instant, for example 2026-10-01T00:00:00.000Z.",
    });
  }
  const until = draft.effectiveUntil.trim();
  if (until.length > 0 && !Number.isFinite(Date.parse(until))) {
    problems.push({
      field: "effectiveUntil",
      message: "Effective until must be an ISO instant, or empty for no end.",
    });
  } else if (until.length > 0 && until <= from) {
    problems.push({ field: "effectiveUntil", message: "Effective end must follow the start." });
  }

  const boundary = boundaryOf(draft);
  if (Array.isArray(boundary)) problems.push(...(boundary as readonly AreaProblem[]));

  if (problems.length > 0) return problems;

  return {
    name,
    marketRef,
    productRefs,
    priority,
    lifecycle: draft.lifecycle,
    effectiveFrom: from,
    ...(until.length > 0 ? { effectiveUntil: until } : {}),
    boundary: boundary as DraftBoundary,
    expectedVersion,
  };
}

/**
 * The selected area, for the panel beside the board.
 *
 * **`priority` and `marketRef` are the two fields this panel exists for.** Where two areas overlap,
 * priority is what decides which one serves a pickup, and the market is what the area belongs to —
 * neither has a column in the design's grid, and a dispatcher asking why a point is served by one
 * area rather than another is asking for exactly those two.
 *
 * **The effective window is drawn in full, including the end that has not got one.** An area with
 * no `effectiveUntil` is open-ended, which is a different statement from an area whose end has
 * passed, and the badge collapses both into one word on rows where the distinction matters. The
 * panel separates them.
 *
 * **A geofence-backed boundary names the pinned version**, for the reason `boundaryText` records:
 * amending the fence does not move the area, because the version is part of the reference. That is
 * the single most surprising fact on this board and it belongs where somebody is reading carefully.
 */
export function areaDetail(area: ServiceArea, now: number): RecordDetail {
  const state = stateOf(area, now);
  const reason = stateReason(area, now);

  return {
    headline: area.name,
    identifier: area.id,
    source: area.boundary.kind === "geofence" ? "GEO-06 · GEO-05" : "GEO-06",
    state: { ...state, code: "GEO-06", status: area.lifecycle },
    fields: [
      field("Lifecycle", area.lifecycle),
      // The two the grid has no column for; see this function's head.
      field("Priority", String(area.priority)),
      field("Market", area.marketRef, true),
      field("Services", coverageText(area)),
      field("Boundary", boundaryText(area)),
      ...(area.boundary.kind === "geofence"
        ? [
            field("Geofence", area.boundary.geofenceId, true),
            field("Geofence version", `v${String(area.boundary.geofenceVersion)}`),
          ]
        : []),
      field("Effective from", at(area.effectiveFrom) ?? area.effectiveFrom),
      ...(area.effectiveUntil === undefined
        ? [
            field("Effective until", "open-ended"),
            /*
             * Said rather than dropped. An area with no end is open-ended; an area whose end has
             * passed is finished. The badge says `no longer effective` for the second and `active`
             * for the first, and a missing line here would leave a reader to infer which.
             */
          ]
        : [field("Effective until", at(area.effectiveUntil) ?? area.effectiveUntil)]),
      field("Created", at(area.createdAt) ?? area.createdAt),
      field("Last changed", at(area.updatedAt) ?? area.updatedAt),
      field("Version", `v${String(area.version)}`),
    ],
    ...(reason === undefined ? {} : { note: reason }),
  };
}
